import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useToast } from '../hooks/use-toast';
import NotificationBell from '../components/NotificationBell';
import MiroTalkRoom from '../components/MiroTalkRoom';
import { 
  Video, 
  Users,
  Calendar,
  Clock,
  Plus,
  ArrowLeft,
  UserPlus,
  Lock,
  Globe,
  Timer,
  Crown,
  Star,
  Zap,
  Play,
  AlertCircle,
  Trash2,
  Edit
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Plan configurations
const PLAN_CONFIG = {
  basic: {
    name: 'Basic',
    duration: 30, // minutes
    maxRoomsPerMonth: 4,
    roomDuration: 30, // 30 minutes per room
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    icon: Clock,
    description: '30 minutes per session'
  },
  premium: {
    name: 'Premium', 
    duration: 120, // 2 hours per room
    maxRoomsPerMonth: 10,
    roomDuration: 120,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    icon: Crown,
    description: '2 hours per session'
  },
  advanced: {
    name: 'Advanced',
    duration: -1, // unlimited
    maxRoomsPerMonth: -1, // unlimited
    roomDuration: -1,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    icon: Star,
    description: 'Unlimited session time'
  }
};

const VirtualRooms = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();
  
  // State management
  const [createRoomOpen, setCreateRoomOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [myProfile, setMyProfile] = useState(null);
  const [allResearchers, setAllResearchers] = useState([]);
  const [roomType, setRoomType] = useState('public');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [inSession, setInSession] = useState(false);
  
  const [roomForm, setRoomForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    duration: '60'
  });
  const [isCreatingRoom, setIsCreatingRoom] = useState(false);
  const [isJoining, setIsJoining] = useState(false); // Prevent duplicate room joins
  const [editingRoom, setEditingRoom] = useState(null); // For edit dialog
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    if (!user) {
      toast({
        title: t('loginRequired'),
        description: t('virtualRoomsLoginRequired'),
        variant: "destructive"
      });
      navigate('/');
    } else {
      fetchMyProfile();
    }
  }, [user, navigate, toast]);

  // Fetch rooms when profile is loaded
  useEffect(() => {
    if (myProfile) {
      fetchRooms();
    }
  }, [myProfile]);

  const fetchMyProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/researchers/user/${user.id}`);
      if (response.ok) {
        const profile = await response.json();
        setMyProfile(profile);
        
        // Fetch all researchers for private room invitations
        const allResponse = await fetch(`${BACKEND_URL}/api/researchers/`);
        if (allResponse.ok) {
          const allProfiles = await allResponse.json();
          setAllResearchers(allProfiles);
        }
      } else if (response.status === 404) {
        toast({
          title: t('profileRequired'),
          description: t('pleaseCreateProfile'),
          variant: "destructive"
        });
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchRooms = async () => {
    try {
      let url = `${BACKEND_URL}/api/virtual-rooms/`;
      if (myProfile?.id) {
        url += `?user_id=${myProfile.id}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setRooms(data);
        console.log(`✅ Fetched ${data.length} rooms for user`);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getSaudiTime = () => {
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Riyadh' }));
  };

  const getRoomDateTime = (room) => {
    return new Date(`${room.date}T${room.time}`);
  };

  const isRoomLive = (room) => {
    const now = getSaudiTime();
    const roomStart = getRoomDateTime(room);
    const roomEnd = new Date(roomStart.getTime() + (parseInt(room.duration) * 60000));
    return now >= roomStart && now <= roomEnd;
  };

  const isRoomUpcoming = (room) => {
    const now = getSaudiTime();
    const roomDateTime = getRoomDateTime(room);
    return roomDateTime > now;
  };

  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const getSaudiDateTime = () => {
    const now = new Date();
    const saudiTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Riyadh' }));
    const date = saudiTime.toISOString().split('T')[0];
    const time = saudiTime.toTimeString().slice(0, 5);
    return { date, time };
  };

  const canJoinRoom = (room) => {
    if (!room.is_private) return true;
    if (!myProfile) return false;
    return room.host_id === myProfile.id || room.allowed_participants.includes(myProfile.id);
  };

  const getUserPlan = () => {
    return myProfile?.subscription_plan || 'basic';
  };

  const getPlanConfig = (plan = null) => {
    const userPlan = plan || getUserPlan();
    return PLAN_CONFIG[userPlan] || PLAN_CONFIG.basic;
  };

  const formatDuration = (minutes) => {
    if (minutes === -1) return t('unlimited');
    if (minutes >= 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
    }
    return `${minutes}m`;
  };

  // Set default date/time when dialog opens
  useEffect(() => {
    if (createRoomOpen && !roomForm.date && !roomForm.time) {
      const { date, time } = getSaudiDateTime();
      setRoomForm(prev => ({ ...prev, date, time }));
    }
  }, [createRoomOpen]);

  // Room creation handler
  const handleCreateRoom = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isCreatingRoom) return;
    
    if (!myProfile) {
      toast({
        title: t('error'),
        description: t('pleaseCreateProfile'),
        variant: "destructive"
      });
      return;
    }

    if (roomType === 'private' && selectedParticipants.length === 0) {
      toast({
        title: t('error'),
        description: t('selectAtLeastOne'),
        variant: "destructive"
      });
      return;
    }

    // Check room creation limits
    const userPlan = getUserPlan();
    const planConfig = getPlanConfig(userPlan);
    
    if (planConfig.maxRoomsPerMonth !== -1) {
      // Count rooms created this month
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      const roomsThisMonth = rooms.filter(room => {
        if (room.host_id !== myProfile.id) return false;
        const roomDate = new Date(room.date);
        return roomDate.getMonth() === currentMonth && roomDate.getFullYear() === currentYear;
      }).length;
      
      if (roomsThisMonth >= planConfig.maxRoomsPerMonth) {
        toast({
          title: t('roomLimitReached'),
          description: t('monthlyRoomLimitExceeded', { limit: planConfig.maxRoomsPerMonth, plan: planConfig.name }),
          variant: "destructive"
        });
        return;
      }
    }

    setIsCreatingRoom(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/virtual-rooms/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...roomForm,
          host_id: myProfile.id,
          host_name: myProfile.name,
          host_plan: getUserPlan(),
          is_private: roomType === 'private',
          allowed_participants: roomType === 'private' ? selectedParticipants : []
        })
      });

      if (response.ok) {
        const newRoom = await response.json();
        toast({
          title: t('success'),
          description: t('roomCreatedSuccessfully')
        });
        
        setRooms(prev => [newRoom, ...prev]);
        setCreateRoomOpen(false);
        setRoomForm({
          title: '',
          description: '',
          date: '',
          time: '',
          duration: '60'
        });
        setRoomType('public');
        setSelectedParticipants([]);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create room');
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: error.message || t('failedToCreateRoom'),
        variant: "destructive"
      });
    } finally {
      setIsCreatingRoom(false);
    }
  };

  // Join room handler
  const handleJoinRoom = async (room) => {
    // Prevent duplicate joins
    if (isJoining) return;
    
    if (!canJoinRoom(room)) {
      toast({
        title: t('accessDenied'),
        description: t('privateRoomAccessDenied'),
        variant: "destructive"
      });
      return;
    }

    setIsJoining(true);

    try {
      await fetch(`${BACKEND_URL}/api/virtual-rooms/${room.id}/join?user_id=${myProfile.id}`, {
        method: 'PUT'
      });
      
      setCurrentRoom(room);
      setInSession(true);
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToJoinRoom'),
        variant: "destructive"
      });
    } finally {
      setIsJoining(false);
    }
  };

  // Delete room handler
  const handleDeleteRoom = async (roomId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/virtual-rooms/${roomId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast({
          title: t('success'),
          description: t('roomDeletedSuccessfully')
        });
        
        // Remove room from state
        setRooms(prev => prev.filter(room => room.id !== roomId));
      } else {
        throw new Error('Failed to delete room');
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToDeleteRoom'),
        variant: "destructive"
      });
    }
  };

  const confirmDeleteRoom = (room) => {
    if (window.confirm(`${t('confirmDeleteRoom')} "${room.title}"?`)) {
      handleDeleteRoom(room.id);
    }
  };

  // Edit room handlers
  const handleEditRoom = (room) => {
    setEditingRoom({
      id: room.id,
      title: room.title,
      description: room.description || '',
      date: room.date,
      time: room.time,
      duration: room.duration,
      is_private: room.is_private,
      allowed_participants: room.allowed_participants || []
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateRoom = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/virtual-rooms/${editingRoom.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: editingRoom.title,
          description: editingRoom.description,
          date: editingRoom.date,
          time: editingRoom.time,
          duration: editingRoom.duration,
          is_private: editingRoom.is_private,
          allowed_participants: editingRoom.allowed_participants
        })
      });

      if (response.ok) {
        toast({
          title: t('success'),
          description: t('roomUpdatedSuccessfully')
        });
        
        setIsEditDialogOpen(false);
        setEditingRoom(null);
        fetchRooms(); // Refresh rooms list
      } else {
        throw new Error('Failed to update room');
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToUpdateRoom'),
        variant: "destructive"
      });
    }
  };


  // Leave room handler
  const handleLeaveRoom = () => {
    setCurrentRoom(null);
    setInSession(false);
    fetchRooms(); // Refresh room data
  };

  // Session end handler
  const handleSessionEnd = () => {
    toast({
      title: t('sessionEnded'),
      description: t('sessionEndedSuccessfully'),
    });
    handleLeaveRoom();
  };

  // Filter rooms
  const activeRooms = rooms.filter(room => isRoomLive(room));
  const upcomingRooms = rooms.filter(room => isRoomUpcoming(room));

  // If in session, show Jitsi component
  if (inSession && currentRoom) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto p-6">
          <MiroTalkRoom
            roomData={currentRoom}
            userProfile={myProfile}
            onLeave={handleLeaveRoom}
            onSessionEnd={handleSessionEnd}
          />
        </div>
      </div>
    );
  }

  // Main rooms listing interface
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 py-8">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-cyan-600 hover:bg-cyan-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('backToHome')}
            </Button>
            <NotificationBell />
          </div>
        </div>

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('virtualRoomsPageTitle')}
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            {t('virtualRoomsPageDesc')}
          </p>
          
          {/* User Plan Display */}
          {myProfile && (
            <div className="flex items-center justify-center gap-4 mb-8">
              <div className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg ${getPlanConfig().bgColor}`}>
                <div className="flex items-center gap-2">
                  {React.createElement(getPlanConfig().icon, { 
                    className: `h-5 w-5 ${getPlanConfig().color}` 
                  })}
                  <span className={`font-semibold ${getPlanConfig().color}`}>
                    {t('currentPlan')}: {getPlanConfig().name}
                  </span>
                  <Badge variant="outline" className="ml-2">
                    {getPlanConfig().description}
                  </Badge>
                </div>
                {getPlanConfig().maxRoomsPerMonth !== -1 && (
                  <div className="text-xs text-gray-600">
                    {t('roomsUsedThisMonth')}: {(() => {
                      const now = new Date();
                      const currentMonth = now.getMonth();
                      const currentYear = now.getFullYear();
                      return rooms.filter(room => {
                        if (room.host_id !== myProfile?.id) return false;
                        const roomDate = new Date(room.date);
                        return roomDate.getMonth() === currentMonth && roomDate.getFullYear() === currentYear;
                      }).length;
                    })()} / {getPlanConfig().maxRoomsPerMonth} {t('roomsThisMonth')}
                  </div>
                )}
              </div>
              
              <Dialog open={createRoomOpen} onOpenChange={setCreateRoomOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-cyan-600 hover:bg-cyan-700 gap-2">
                    <Plus className="h-4 w-4" />
                    {t('createRoom')}
                  </Button>
                </DialogTrigger>
                
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{t('createNewRoom')}</DialogTitle>
                    <DialogDescription>
                      {t('createRoomDescription')}
                    </DialogDescription>
                  </DialogHeader>

                  <form onSubmit={handleCreateRoom} className="space-y-6" onKeyPress={(e) => { if (e.key === 'Enter' && e.target.type !== 'submit') e.preventDefault(); }}>
                    {/* Plan Info */}
                    <div className={`p-4 rounded-lg ${getPlanConfig().bgColor}`}>
                      <div className="flex items-center gap-3">
                        {React.createElement(getPlanConfig().icon, { 
                          className: `h-6 w-6 ${getPlanConfig().color}` 
                        })}
                        <div>
                          <h3 className={`font-semibold ${getPlanConfig().color}`}>
                            {getPlanConfig().name} {t('plan')}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {t('sessionLimit')}: {getPlanConfig().description}
                          </p>
                          <p className="text-sm text-gray-600">
                            {t('monthlyRoomLimit')}: {getPlanConfig().maxRoomsPerMonth === -1 ? t('unlimited') : `${getPlanConfig().maxRoomsPerMonth} ${t('roomsPerMonth')}`}
                          </p>
                          {getPlanConfig().maxRoomsPerMonth !== -1 && (
                            <p className="text-xs text-gray-500 mt-1">
                              {t('roomsUsedThisMonth')}: {(() => {
                                const now = new Date();
                                const currentMonth = now.getMonth();
                                const currentYear = now.getFullYear();
                                return rooms.filter(room => {
                                  if (room.host_id !== myProfile?.id) return false;
                                  const roomDate = new Date(room.date);
                                  return roomDate.getMonth() === currentMonth && roomDate.getFullYear() === currentYear;
                                }).length;
                              })()} / {getPlanConfig().maxRoomsPerMonth}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Basic Room Info */}
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <Label htmlFor="title">{t('roomTitle')}</Label>
                        <Input
                          id="title"
                          value={roomForm.title}
                          onChange={(e) => setRoomForm(prev => ({...prev, title: e.target.value}))}
                          placeholder={t('enterRoomTitle')}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="description">{t('description')}</Label>
                        <Input
                          id="description"
                          value={roomForm.description}
                          onChange={(e) => setRoomForm(prev => ({...prev, description: e.target.value}))}
                          placeholder={t('optionalDescription')}
                        />
                      </div>
                    </div>

                    {/* Date and Time */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="date">{t('date')}</Label>
                        <Input
                          id="date"
                          type="date"
                          value={roomForm.date}
                          onChange={(e) => setRoomForm(prev => ({...prev, date: e.target.value}))}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="time">{t('time')}</Label>
                        <Input
                          id="time"
                          type="time"
                          value={roomForm.time}
                          onChange={(e) => setRoomForm(prev => ({...prev, time: e.target.value}))}
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="duration">{t('plannedDuration')}</Label>
                        <Select 
                          value={roomForm.duration} 
                          onValueChange={(value) => setRoomForm(prev => ({...prev, duration: value}))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="30">30 {t('minutes')}</SelectItem>
                            <SelectItem value="60">1 {t('hour')}</SelectItem>
                            <SelectItem value="90">1.5 {t('hours')}</SelectItem>
                            <SelectItem value="120">2 {t('hours')}</SelectItem>
                            {getUserPlan() !== 'basic' && (
                              <>
                                <SelectItem value="180">3 {t('hours')}</SelectItem>
                                <SelectItem value="240">4 {t('hours')}</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Privacy Settings */}
                    <div>
                      <Label>{t('roomPrivacy')}</Label>
                      <div className="mt-2 space-y-3">
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="public"
                            name="roomType"
                            value="public"
                            checked={roomType === 'public'}
                            onChange={(e) => setRoomType(e.target.value)}
                            className="text-cyan-600"
                          />
                          <Label htmlFor="public" className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            {t('publicRoom')} - {t('anyoneCanJoin')}
                          </Label>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <input
                            type="radio"
                            id="private"
                            name="roomType"
                            value="private"
                            checked={roomType === 'private'}
                            onChange={(e) => setRoomType(e.target.value)}
                            className="text-cyan-600"
                          />
                          <Label htmlFor="private" className="flex items-center gap-2">
                            <Lock className="h-4 w-4" />
                            {t('privateRoom')} - {t('inviteOnly')}
                          </Label>
                        </div>
                      </div>
                    </div>

                    {/* Participant Selection for Private Rooms */}
                    {roomType === 'private' && (
                      <div>
                        <Label>{t('inviteParticipants')}</Label>
                        <div className="mt-2 max-h-32 overflow-y-auto border rounded-md p-3 bg-gray-50">
                          {allResearchers
                            .filter(r => r.id !== myProfile?.id)
                            .map((researcher) => (
                              <div key={researcher.id} className="flex items-center space-x-2 py-1">
                                <Checkbox
                                  checked={selectedParticipants.includes(researcher.id)}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setSelectedParticipants(prev => [...prev, researcher.id]);
                                    } else {
                                      setSelectedParticipants(prev => prev.filter(id => id !== researcher.id));
                                    }
                                  }}
                                />
                                <Label className="text-sm">
                                  {researcher.name} ({researcher.university})
                                </Label>
                              </div>
                            ))}
                        </div>
                        {selectedParticipants.length > 0 && (
                          <p className="text-sm text-green-600 mt-2">
                            {selectedParticipants.length} {t('participantsSelected')}
                          </p>
                        )}
                      </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button 
                        type="button" 
                        variant="outline" 
                        onClick={() => setCreateRoomOpen(false)}
                      >
                        {t('cancel')}
                      </Button>
                      <Button type="submit" className="bg-cyan-600 hover:bg-cyan-700" disabled={isCreatingRoom}>
                        <Plus className="h-4 w-4 mr-2" />
                        {isCreatingRoom ? t('creating...') : t('createRoom')}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Live Rooms Section */}
        {activeRooms.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <Video className="h-6 w-6 text-red-600" />
              <h2 className="text-2xl font-bold text-gray-900">{t('liveRooms')}</h2>
              <Badge className="bg-red-100 text-red-800">
                {activeRooms.length} {t('active')}
              </Badge>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeRooms.map((room) => {
                const planConfig = getPlanConfig(room.host_plan);
                return (
                  <Card key={room.id} className="border-l-4 border-l-red-500 hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{room.title}</CardTitle>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                            {React.createElement(planConfig.icon, { 
                              className: `h-4 w-4 ${planConfig.color}` 
                            })}
                            <span>{planConfig.name}</span>
                            {room.is_private ? (
                              <Badge variant="secondary" className="text-xs">
                                <Lock className="h-3 w-3 mr-1" />
                                {t('private')}
                              </Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                <Globe className="h-3 w-3 mr-1" />
                                {t('public')}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Badge className="bg-red-100 text-red-800">
                          <div className="w-2 h-2 bg-red-600 rounded-full mr-1 animate-pulse" />
                          {t('live')}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-3">
                        {room.description && (
                          <p className="text-sm text-gray-600">{room.description}</p>
                        )}
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Users className="h-4 w-4" />
                              {room.participants}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {formatTime(room.time)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="text-xs text-gray-500">
                          {t('hostedBy')}: {room.host_name}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            onClick={() => handleJoinRoom(room)}
                            disabled={!canJoinRoom(room) || isJoining}
                            className="flex-1 bg-red-600 hover:bg-red-700"
                          >
                            <Play className="h-4 w-4 mr-2" />
                            {isJoining ? t('joining') : t('joinNow')}
                          </Button>
                          
                          {/* Edit and Delete buttons - only for room creator */}
                          {myProfile && room.host_id === myProfile.id && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditRoom(room);
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDeleteRoom(room);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Upcoming Rooms Section */}
        {upcomingRooms.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <Calendar className="h-6 w-6 text-blue-600" />
              <h2 className="text-2xl font-bold text-gray-900">{t('upcomingSessions')}</h2>
              <Badge className="bg-blue-100 text-blue-800">
                {upcomingRooms.length} {t('scheduled')}
              </Badge>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingRooms.map((room) => {
                const planConfig = getPlanConfig(room.host_plan);
                const roomDateTime = getRoomDateTime(room);
                return (
                  <Card key={room.id} className="border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{room.title}</CardTitle>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                            {React.createElement(planConfig.icon, { 
                              className: `h-4 w-4 ${planConfig.color}` 
                            })}
                            <span>{planConfig.name}</span>
                            {room.is_private ? (
                              <Badge variant="secondary" className="text-xs">
                                <Lock className="h-3 w-3 mr-1" />
                                {t('private')}
                              </Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                <Globe className="h-3 w-3 mr-1" />
                                {t('public')}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800">
                          {t('upcoming')}
                        </Badge>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-3">
                        {room.description && (
                          <p className="text-sm text-gray-600">{room.description}</p>
                        )}
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {roomDateTime.toLocaleDateString()}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" />
                              {formatTime(room.time)}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-gray-500">
                            {t('hostedBy')}: {room.host_name}
                          </span>
                          <span className="text-gray-500">
                            {t('duration')}: {room.duration}m
                          </span>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            disabled
                            variant="outline"
                            className="flex-1"
                          >
                            <Clock className="h-4 w-4 mr-2" />
                            {t('startsAt')} {formatTime(room.time)}
                          </Button>
                          
                          {/* Edit and Delete buttons - only for room creator */}
                          {myProfile && room.host_id === myProfile.id && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditRoom(room);
                                }}
                                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  confirmDeleteRoom(room);
                                }}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && rooms.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Video className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {t('noRoomsYet')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('beFirstToCreateRoom')}
              </p>
              <Button 
                onClick={() => setCreateRoomOpen(true)}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('createFirstRoom')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          </div>
        )}

        {/* Plan Upgrade CTA */}
        {myProfile && getUserPlan() === 'basic' && (
          <Card className="mt-12 border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-100 rounded-full">
                    <Zap className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {t('upgradeForMoreRoomsAndTime')}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {t('basicPlanLimits')}: 4 {t('roomsPerMonth')}, 30 {t('minutesPerSession')}
                    </p>
                    <p className="text-sm text-gray-600">
                      {t('premiumPlanBenefits')}: 10 {t('roomsPerMonth')}, 2 {t('hoursPerSession')}
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/subscription')}
                  className="bg-yellow-600 hover:bg-yellow-700"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  {t('upgradePlan')}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Edit Room Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('editRoom')}</DialogTitle>
              <DialogDescription>{t('editRoomDescription')}</DialogDescription>
            </DialogHeader>
            
            {editingRoom && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-title">{t('roomTitle')}</Label>
                  <Input
                    id="edit-title"
                    value={editingRoom.title}
                    onChange={(e) => setEditingRoom({...editingRoom, title: e.target.value})}
                    placeholder={t('enterRoomTitle')}
                  />
                </div>

                <div>
                  <Label htmlFor="edit-description">{t('description')}</Label>
                  <Input
                    id="edit-description"
                    value={editingRoom.description}
                    onChange={(e) => setEditingRoom({...editingRoom, description: e.target.value})}
                    placeholder={t('optionalDescription')}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-date">{t('date')}</Label>
                    <Input
                      id="edit-date"
                      type="date"
                      value={editingRoom.date}
                      onChange={(e) => setEditingRoom({...editingRoom, date: e.target.value})}
                    />
                  </div>

                  <div>
                    <Label htmlFor="edit-time">{t('time')}</Label>
                    <Input
                      id="edit-time"
                      type="time"
                      value={editingRoom.time}
                      onChange={(e) => setEditingRoom({...editingRoom, time: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-duration">{t('duration')}</Label>
                  <Select 
                    value={editingRoom.duration} 
                    onValueChange={(value) => setEditingRoom({...editingRoom, duration: value})}
                  >
                    <SelectTrigger id="edit-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="30">30 {t('minutes')}</SelectItem>
                      <SelectItem value="60">60 {t('minutes')}</SelectItem>
                      <SelectItem value="90">90 {t('minutes')}</SelectItem>
                      <SelectItem value="120">120 {t('minutes')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="edit-private"
                    checked={editingRoom.is_private}
                    onCheckedChange={(checked) => setEditingRoom({...editingRoom, is_private: checked})}
                  />
                  <Label htmlFor="edit-private" className="cursor-pointer">
                    {t('makeRoomPrivate')}
                  </Label>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsEditDialogOpen(false);
                      setEditingRoom(null);
                    }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handleUpdateRoom}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {t('saveChanges')}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
};

export default VirtualRooms;